import React, { useState, useEffect } from "react";
import { Plus, Edit, Trash2, Users, Target, Building2, X } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Badge } from "../components/ui/badge";
import { apiRequest, formatDate, formatPhone } from "../lib/utils";
import { toast } from "sonner";

export const CREManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    role: "cre",
    branch_id: "",
    branch_ids: [],
    is_active: true,
    target_mode: "auto",
    manual_target: 50
  });

  useEffect(() => {
    fetchUsers();
    fetchBranches();
  }, []);

  const fetchUsers = async () => {
    try {
      const data = await apiRequest("/users");
      setUsers(data);
    } catch (error) {
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const data = await apiRequest("/branches");
      setBranches(data);
    } catch (error) {
      console.error("Failed to fetch branches:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const updateData = { ...formData };
        delete updateData.password;
        await apiRequest(`/users/${editingUser.id}`, {
          method: "PUT",
          body: JSON.stringify(updateData)
        });
        toast.success("User updated successfully");
      } else {
        await apiRequest("/auth/register", {
          method: "POST",
          body: JSON.stringify(formData)
        });
        toast.success("User created successfully");
      }
      closeModal();
      fetchUsers();
    } catch (error) {
      toast.error(error.message || "Failed to save user");
    }
  };

  const handleDelete = async () => {
    try {
      await apiRequest(`/users/${deletingUser.id}`, {
        method: "DELETE"
      });
      toast.success("User deleted successfully");
      setDeletingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: "",
      phone: user.phone || "",
      role: user.role,
      branch_id: user.branch_id || "",
      branch_ids: user.branch_ids || [],
      is_active: user.is_active !== false,
      target_mode: user.target_mode || "auto",
      manual_target: user.manual_target || 50
    });
    setShowAddModal(true);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingUser(null);
    setFormData({
      name: "",
      email: "",
      password: "",
      phone: "",
      role: "cre",
      branch_id: "",
      branch_ids: [],
      is_active: true,
      target_mode: "auto",
      manual_target: 50
    });
  };

  const getBranchName = (branchId) => {
    const branch = branches.find((b) => b.id === branchId);
    return branch?.name || "-";
  };

  const getBranchNames = (branchId, branchIds = []) => {
    const allIds = [branchId, ...branchIds].filter(Boolean);
    const uniqueIds = [...new Set(allIds)];
    return uniqueIds.map(id => getBranchName(id)).filter(n => n !== "-");
  };

  const toggleBranch = (branchId) => {
    const current = formData.branch_ids || [];
    if (current.includes(branchId)) {
      setFormData({ ...formData, branch_ids: current.filter(id => id !== branchId) });
    } else {
      setFormData({ ...formData, branch_ids: [...current, branchId] });
    }
  };

  return (
    <div className="space-y-6" data-testid="cre-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">CRE Management</h1>
          <p className="text-muted-foreground">Manage your team members and branch assignments</p>
        </div>
        <Dialog open={showAddModal} onOpenChange={(open) => {
          if (open) {
            setShowAddModal(true);
          } else {
            closeModal();
          }
        }}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-button" onClick={() => setShowAddModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingUser ? "Edit User" : "Add New User"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="user-name-input"
                />
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  disabled={!!editingUser}
                  data-testid="user-email-input"
                />
              </div>
              {!editingUser && (
                <div>
                  <Label htmlFor="password">Password *</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    data-testid="user-password-input"
                  />
                </div>
              )}
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="user-phone-input"
                />
              </div>
              <div>
                <Label htmlFor="role">Role</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cre">CRE</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Multi-Branch Assignment */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-muted-foreground" />
                  <Label className="font-medium">Branch Assignment</Label>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Primary Branch</Label>
                  <Select
                    value={formData.branch_id}
                    onValueChange={(value) => setFormData({ ...formData, branch_id: value })}
                  >
                    <SelectTrigger data-testid="user-branch-select">
                      <SelectValue placeholder="Select primary branch" />
                    </SelectTrigger>
                    <SelectContent>
                      {branches.map((branch) => (
                        <SelectItem key={branch.id} value={branch.id}>
                          {branch.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-sm text-muted-foreground">Additional Branches (Multi-select)</Label>
                  <div className="border rounded-md p-3 mt-1 space-y-2 max-h-40 overflow-y-auto">
                    {branches.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No branches available</p>
                    ) : (
                      branches.map((branch) => (
                        <div key={branch.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`branch-${branch.id}`}
                            checked={(formData.branch_ids || []).includes(branch.id)}
                            onCheckedChange={() => toggleBranch(branch.id)}
                            disabled={branch.id === formData.branch_id}
                            data-testid={`branch-checkbox-${branch.id}`}
                          />
                          <Label 
                            htmlFor={`branch-${branch.id}`} 
                            className={`text-sm cursor-pointer ${branch.id === formData.branch_id ? 'text-muted-foreground' : ''}`}
                          >
                            {branch.name}
                            {branch.id === formData.branch_id && (
                              <span className="text-xs ml-1">(Primary)</span>
                            )}
                          </Label>
                        </div>
                      ))
                    )}
                  </div>
                  {(formData.branch_ids || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {formData.branch_ids.map(bid => (
                        <Badge key={bid} variant="secondary" className="text-xs">
                          {getBranchName(bid)}
                          <button 
                            type="button"
                            onClick={() => toggleBranch(bid)}
                            className="ml-1 hover:text-destructive"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <Label htmlFor="is_active">Active Status</Label>
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  data-testid="user-active-switch"
                />
              </div>
              
              {/* Target Mode - Only for CRE role */}
              {formData.role === "cre" && (
                <div className="space-y-4 pt-4 border-t">
                  <div className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-muted-foreground" />
                    <Label className="font-medium">Call Target Settings</Label>
                  </div>
                  <RadioGroup
                    value={formData.target_mode}
                    onValueChange={(value) => setFormData({ ...formData, target_mode: value })}
                    className="grid grid-cols-2 gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="auto" id="target-auto" data-testid="target-mode-auto" />
                      <Label htmlFor="target-auto" className="cursor-pointer">
                        <span className="font-medium">Auto Target</span>
                        <p className="text-xs text-muted-foreground">System calculated</p>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="manual" id="target-manual" data-testid="target-mode-manual" />
                      <Label htmlFor="target-manual" className="cursor-pointer">
                        <span className="font-medium">Manual Target</span>
                        <p className="text-xs text-muted-foreground">Admin-defined</p>
                      </Label>
                    </div>
                  </RadioGroup>
                  
                  {formData.target_mode === "manual" && (
                    <div>
                      <Label htmlFor="manual_target">Daily Call Target</Label>
                      <Input
                        id="manual_target"
                        type="number"
                        min={1}
                        max={200}
                        value={formData.manual_target}
                        onChange={(e) => setFormData({ ...formData, manual_target: parseInt(e.target.value) || 50 })}
                        className="mt-1"
                        data-testid="manual-target-input"
                      />
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={closeModal}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-user-button">
                  {editingUser ? "Update" : "Create"} User
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Users Table */}
      <Card className="bento-card">
        <CardContent className="p-0">
          <div className="data-table-container">
            <Table>
              <TableHeader>
                <TableRow className="table-high-density">
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Branches</TableHead>
                  <TableHead>Target Mode</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8">
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <Users className="w-10 h-10 opacity-50" />
                        <p>No users found</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => {
                    const branchNames = getBranchNames(user.branch_id, user.branch_ids);
                    return (
                      <TableRow key={user.id} className="table-high-density" data-testid={`user-row-${user.id}`}>
                        <TableCell className="font-medium">{user.name}</TableCell>
                        <TableCell className="text-sm">{user.email}</TableCell>
                        <TableCell className="font-mono text-sm">
                          {user.phone ? formatPhone(user.phone) : "-"}
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            user.role === "admin"
                              ? "bg-purple-100 text-purple-800"
                              : "bg-blue-100 text-blue-800"
                          }`}>
                            {user.role?.toUpperCase()}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1 max-w-[200px]">
                            {branchNames.length > 0 ? (
                              branchNames.map((name, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">
                                  {name}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-muted-foreground text-sm">-</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {user.role === "cre" && (
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              user.target_mode === "manual"
                                ? "bg-orange-100 text-orange-800"
                                : "bg-cyan-100 text-cyan-800"
                            }`} data-testid={`target-mode-${user.id}`}>
                              {user.target_mode === "manual" ? `Manual (${user.manual_target || 50})` : "Auto"}
                            </span>
                          )}
                          {user.role === "admin" && (
                            <span className="text-muted-foreground text-xs">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            user.is_active !== false
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                          }`}>
                            {user.is_active !== false ? "Active" : "Inactive"}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(user.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditModal(user)}
                              data-testid={`edit-user-${user.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setDeletingUser(user)}
                              className="text-destructive hover:text-destructive"
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingUser} onOpenChange={() => setDeletingUser(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deletingUser?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CREManagementPage;
